import { defineComponent, onMounted, ref, watch, onBeforeUnmount, Ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Container } from "aurelia-dependency-injection";
import { useDialog } from "primevue/usedialog";
import { UserManager } from "oidc-client";

import { useApp } from "../../useApp";
import { useCallStore } from "../../../MessageCenter/store/useCallStore";
import { useUserStore } from "../../../MessageCenter/store/useUsersStore";
import { useConversationStore } from "../../../MessageCenter/store/useConversationStore";
import { useMessagesStore } from "../../../MessageCenter/store/useMessagesStore";
import { SignalRService } from "../../../../services/signal-r-service";
import { MessageService } from "../../../../services/message-service";
import { ToastrService } from "../../../../services/toastr.service";
import { FamilyPortalService } from "../../../../services/family-portal-service";
import {
    AutoGeneratedMessageEnum,
    CallNotificationResponseTypeEnum,
    ChatNavOptionsEnum,
    jitsiConferenceStatus,
    NotificationTypeEnum
} from "../../../../enums/enums";
import {
    ICallControls,
    IFpCallNotificationResponse,
    ICallNotificationResponse,
    ICallSignalNotification,
    ICallStatusChange,
    IReply,
    IReplyHistory,
    IReplyState,
    IDeleteNotification,
    IOutgoingCallProps,
    IMessageReactionNotification,
    IStatusChangeNotification,
    IReadConversationNotification
} from "../../../../resources-vue/vue-interfaces/messaging-service/i-notification";
import { IPromptOptions } from "../../../../resources-vue/vue-interfaces/i-prompt";
import { INotification } from "../../../../resources-vue/vue-interfaces/messaging-service/i-notification";
import { SignalRHubs } from "../../../../enums/SignalRHubs";
import { NotificationsHubEvents } from "../../../../enums/NotificationsHubEvents";
import { ReactionEmojisEnum } from "../../../../enums/ReactionEmojisEnum";
import {
    IMessagePlaceholder,
    ISendMessageQuery
} from "../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { markdownToHtml } from "../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../../common/vue-helpers/modifiers/value-modifier";
import config from "../../../../common/config";
import eventBus from "../../../../utilities/eventBus";
import { truncateText } from "../../../../utilities/truncateString";
import callRingToneSrc from "../../../../assets/audio/ringtone-1.mp3";
import notificationSoundSrc from "../../../../assets/audio/message-notification.mp3";
import { sortPlaceholdersList } from "../../../../common/vue-helpers/modifiers/format-message";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";

import CallNotification from "./call-notification.vue";
import OutgoingCallNotification from "./OutgoingCallNotification/OutgoingCallNotification.vue";
import Prompt from "../../../../resources-vue/vue-dialogs/prompt/prompt.vue";

const NOTIFICATION_TIMEOUT = 7000;

export default defineComponent({
    name: "Notification",
    components: {
        CallNotification,
        OutgoingCallNotification
    },
    setup() {
        const router = useRouter();
        const route = useRoute();
        const appStore = useApp();
        const userStore = useUserStore();
        const convStore = useConversationStore();
        const messageStore = useMessagesStore();
        const callStore = useCallStore();
        const dialog = useDialog();
        const notifications = ref<INotification[]>([]);
        const signalRService = ref<SignalRService | null>(null);
        const _toastrService = ref<ToastrService | null>(null);
        const _analyticsService = ref<AnalyticsService | null>(null);
        const userManager = ref<UserManager | null>(null);
        const callControls: Ref<ICallControls> = ref(null);
        const isFamilyPortalCall: Ref<boolean> = ref(false);
        const audio = ref<HTMLAudioElement | null>(null);
        const hasJoinMeetingBtn = ref(false);
        const messageService = ref<MessageService | null>(null);
        const familyPortalService = ref<FamilyPortalService | null>(null);
        const chatType = ref<NotificationTypeEnum | null>(null);
        const callSignalRNotification: Ref<ICallSignalNotification> = ref(null);
        const outgoingCallNotification: Ref<IOutgoingCallProps> = ref(null);
        const recipientDeclined = ref(false);
        const lastStartedCall = ref<{ roomId: string; startedAt: number } | null>(null);

        // Reply-related reactive variables
        const replyStates = ref<Map<string, IReplyState>>(new Map());
        const replyHistory = ref<Map<string, IReplyHistory>>(new Map());

        watch(
            notifications,
            (newNotifications) => {
                if (newNotifications.length < 1) {
                    stopAudio();
                }
            },
            { deep: true }
        );

        watch(
            callControls,
            (updatedCallControls) => {
                if (updatedCallControls) {
                    eventBus.emit("updateCallControls", callControls.value);
                }
            },
            { deep: true }
        );

        const scheduleNotificationRemoval = (_conversationId: string, fromPopup: boolean = false) => {
            stopAudio();

            setTimeout(
                () => {
                    const replyState = replyStates.value.get(_conversationId);

                    // Only remove if not replying or reacting
                    if ((!replyState?.isReplying && !replyState?.isReacting) || fromPopup) {
                        const index = notifications.value.findIndex(
                            (notification: INotification) => notification.conversationId === _conversationId
                        );
                        if (index > -1) {
                            notifications.value.splice(index, 1);
                        }
                    }
                    // else: skip removal — user is still replying
                },
                fromPopup ? 0 : NOTIFICATION_TIMEOUT
            );
        };

        const createAndClickButton = async () => {
            return new Promise<void>((resolve) => {
                const newButton = document.createElement("button");
                newButton.style.display = "none";
                document.body.appendChild(newButton);
                newButton.addEventListener("click", () => {
                    resolve();
                });
                newButton.click();
                document.body.removeChild(newButton);
            });
        };

        const navigateToConversation = async (notification: INotification) => {
            await convStore.markConversationAsRead(notification.conversationId);
            router.push(
                `/central-messaging/clinicians/conversation/${notification.conversationId}?tab=${ChatNavOptionsEnum.Chat}`
            );
            scheduleNotificationRemoval(notification.conversationId, true);
        };

        const closeChatNotification = (conversationId: string) => {
            getReplyState(conversationId).isReplying = false;
            scheduleNotificationRemoval(conversationId, true);
        };

        const playAudioMultipleTimes = async (times: number) => {
            await createAndClickButton();
            let playCount = 0;
            audio.value = new Audio(callRingToneSrc);
            audio.value.addEventListener("ended", async () => {
                playCount++;
                if (playCount < times) {
                    audio.value!.currentTime = 0;
                    audio.value!.play().catch(console.error);
                } else {
                    closeNotificationPopup();
                }
            });

            audio.value.play().catch(console.error);
        };

        const playNotificationSound = async (volume: number = 1.0) => {
            await createAndClickButton();
            const notificationAudio = new Audio(notificationSoundSrc);
            notificationAudio.volume = volume;
            notificationAudio.play().catch(console.error);
        };

        const stopAudio = () => {
            if (audio.value) {
                audio.value.pause();
                audio.value.currentTime = 0;
            }
        };

        const emitStartJitsiCall = (roomId: string, isRetryCall: boolean, isFPCall: boolean = false) => {
            const now = Date.now();
            const lastStart = lastStartedCall.value;

            // NotificationsHub can emit more than one acceptance signal for the same room.
            if (lastStart?.roomId === roomId && now - lastStart.startedAt < 3000) {
                return;
            }

            lastStartedCall.value = { roomId, startedAt: now };
            eventBus.emit("startJitiCall", { roomId, isRetryCall, isFamilyPortalCall: isFPCall });
        };

        const initialiseSignalR = async () => {
            signalRService.value = Container.instance.get(SignalRService);

            await signalRService.value.addHub(
                SignalRHubs.NotificationsHub,
                `${config.messagingServiceServerURL}/notification`
            );
            console.info(`Starting ${SignalRHubs.NotificationsHub}...`);
            await signalRService.value.startHub(SignalRHubs.NotificationsHub);
            console.info(`${SignalRHubs.NotificationsHub} started successfully.`);
        };

        const formatTagUserMsg = (text: string, placeholdersList: IMessagePlaceholder[]) => {
            if (placeholdersList?.length > 0) {
                let formattedText: string = text;
                placeholdersList.forEach((user: any) => {
                    formattedText = formattedText.replace("%{TAGGED_USER}%", `${user.name}`);
                });
                return formattedText;
            }
            return text;
        };

        const setUpNotificationEvents = async () => {
            try {
                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.ReceiveMessage,
                    async (notification: INotification) => {
                        console.log("notification => 130", notification);
                        if (notification.autoMessageType !== AutoGeneratedMessageEnum.CallDeclined) {
                            let routeIncludesConversationId = route.fullPath?.includes(notification.conversationId);

                            if (
                                !routeIncludesConversationId &&
                                typeof notification === "object" &&
                                !notification.isMuted
                            ) {
                                // data used to display notification pop-up
                                const { taggedUsers } = sortPlaceholdersList(notification.placeholders);
                                const text = formatTagUserMsg(notification.messageBody, taggedUsers);
                                notification.messageBody = truncateText(text, 100);
                                notification.chatType = NotificationTypeEnum.Text;
                                chatType.value = NotificationTypeEnum.Text;
                                notifications.value.push(notification);
                                notification.clicked = false;
                                await playNotificationSound();
                                scheduleNotificationRemoval(notification.conversationId);
                            }

                            if (routeIncludesConversationId) {
                                await messageStore.receivedNewMsgInActiveConv(notification);
                                await convStore.markConversationAsRead(notification.conversationId);
                            }

                            const msg = {
                                text: notification.messageBody,
                                placeHoldersList: notification.placeholders,
                                modifiedOn: new Date(notification.createdAt).toISOString().slice(0, -1),
                                isImportant: notification.isImportant,
                                id: notification.messageId,
                                unread: routeIncludesConversationId ? 0 : 1
                            };

                            await convStore.updateSideNavMessage(notification.conversationId, msg);
                            convStore.moveConversationToTop(notification.conversationId);
                            const msgs = messageStore.conversationMessages[notification.conversationId];
                            if (msgs) {
                                await messageStore.fetchNewerMessages(notification.conversationId);
                            } else {
                                await messageStore.fetchMessages(notification.conversationId, 25);
                            }
                            if (!notification.isMuted && !routeIncludesConversationId) {
                                // add to unread msgs dropdown
                                convStore.addUnreadConversation(notification.conversationId);
                            }
                        }
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.DeleteMessage,
                    async (notification: IDeleteNotification) => {
                        messageStore.deleteMessage(notification.messageId, notification.conversationId);
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.EditMessage,
                    async (notification: INotification) => {
                        messageStore.editMessage(notification);
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.ReadConversationNotification,
                    async (notification: IReadConversationNotification) => {
                        messageStore.addReadUser(notification);
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.UndoDeleteMessage,
                    async (notification: IDeleteNotification) => {
                        await messageStore.undoDeleteMessage(notification.messageId, notification.conversationId);
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.AddedToConversation,
                    async (notification: INotification) => {
                        const routeIncludesConversationId = route.fullPath?.includes(notification.conversationId);
                        await convStore.updateConversationUsers(notification.conversationId, "added");
                        if (routeIncludesConversationId) {
                            await messageStore.fetchNewerMessages(notification.conversationId);
                        }
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.LeftConversation,
                    async (notification: INotification) => {
                        const routeIncludesConversationId = route.fullPath?.includes(notification.conversationId);
                        await convStore.updateConversationUsers(
                            notification.conversationId,
                            "left",
                            notification.userId
                        );
                        if (routeIncludesConversationId) {
                            await messageStore.fetchNewerMessages(notification.conversationId);
                        }
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.EditConversationTitle,
                    async (notification: INotification) => {
                        convStore.updateConversationTitle(notification.conversationId, notification.newTitle);
                        const routeIncludesConversationId = route.fullPath?.includes(notification.conversationId);

                        if (routeIncludesConversationId) {
                            await messageStore.fetchNewerMessages(notification.conversationId);
                        }
                    }
                );

                signalRService.value.on(SignalRHubs.NotificationsHub, NotificationsHubEvents.ReadAllMessages, () => {
                    convStore.markAllConvAsRead();
                });

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.MessageReactionNotification,
                    async (notification: IMessageReactionNotification) => {
                        messageStore.updateMessageReactions(notification);
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.UserStatusChange,
                    async (notification: IStatusChangeNotification) => {
                        // BE sends loginId inside userId parameter
                        const { userId, ...rest } = notification;
                        userStore.setUserStatus(userId, rest);
                    }
                );
            } catch (error) {
                console.error("Error running SignalR service:", error);
            }
        };

        const setUpNotificationCallEvents = async () => {
            try {
                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.NewCallNotification,
                    async (callNotification: ICallSignalNotification) => {
                        callSignalRNotification.value = callNotification;
                        await playAudioMultipleTimes(1);
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.CallStatusChange,
                    async (callStatus: ICallStatusChange) => {
                        if (
                            callSignalRNotification.value &&
                            callStatus.conferenceStatus === jitsiConferenceStatus.Ended
                        ) {
                            closeNotificationPopup();
                        } else if (
                            outgoingCallNotification.value &&
                            callStatus.conferenceStatus === jitsiConferenceStatus.Ongoing
                        ) {
                            const isFPCall = outgoingCallNotification.value?.isFamilyPortalCall === true;
                            outgoingCallNotification.value = null;
                            stopAudio();
                            callControls.value = null;
                            const isRetryCall =
                                callStore.outgoingCallInfo?.convId === callStatus.conversationId
                                    ? callStore.outgoingCallInfo.isRetryCall
                                    : false;

                            emitStartJitsiCall(callStatus.roomId, isRetryCall, isFPCall);
                        }
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.CallNotificationResponse,
                    async (callNotification: ICallNotificationResponse) => {
                        if (
                            callNotification.callNotificationResponseType == CallNotificationResponseTypeEnum.Declined
                        ) {
                            recipientDeclined.value = true;
                            callStore.setCurrentCallDetails({
                                convId: callStore.currentCallDetails.convId,
                                isUseronCall: false,
                                isFpCall: callStore.currentCallDetails.isFPCall,
                                roomId: callNotification.roomId
                            });
                            eventBus.emit("updateDisableCallBtn", false);
                            callSignalRNotification.value = null;
                            stopAudio();
                        }
                    }
                );

                signalRService.value.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.FpCallNotificationResponse,
                    async (callNotification: IFpCallNotificationResponse) => {
                        if (!outgoingCallNotification.value?.isFamilyPortalCall) {
                            return;
                        }

                        if (
                            callNotification.callNotificationResponseType === CallNotificationResponseTypeEnum.Declined
                        ) {
                            recipientDeclined.value = true;
                            eventBus.emit("updateDisableCallBtn", false);
                            stopAudio();
                            return;
                        }

                        if (
                            callNotification.callNotificationResponseType === CallNotificationResponseTypeEnum.Accepted
                        ) {
                            outgoingCallNotification.value = null;
                            stopAudio();
                            callControls.value = null;
                            const isRetryCall =
                                callStore.outgoingCallInfo?.convId === callNotification.conversationGroupId
                                    ? callStore.outgoingCallInfo.isRetryCall
                                    : false;

                            emitStartJitsiCall(callNotification.roomId, isRetryCall, true);
                        }
                    }
                );
            } catch (error) {
                console.error("Error running SignalR service:", error);
            }
        };

        const handleCallDecline = async () => {
            try {
                const data = { ...callSignalRNotification.value };
                closeNotificationPopup();
                await messageService.value.declineCall(data.conversationId, data.roomId);
            } catch (error) {
                console.error(error);
            }
        };

        const handleOutgoingCallDecline = async (closeNotification: boolean = true) => {
            try {
                if (isFamilyPortalCall.value) {
                    const data = { ...outgoingCallNotification?.value };
                    await familyPortalService.value.declineCall(data?.roomId);
                } else {
                    await messageService.value.declineCall(
                        outgoingCallNotification.value.conversationId,
                        outgoingCallNotification.value.roomId
                    );
                }
                eventBus.emit("updateDisableCallBtn", false);
                if (closeNotification) closeOutgoingPopup();
            } catch (error) {
                console.error(error);
            }
        };
        const handleCallUnanswered = async () => {
            stopAudio();
            try {
                callStore.setCurrentCallDetails({
                    convId: callStore.currentCallDetails.convId,
                    isUseronCall: false,
                    isFpCall: callStore.currentCallDetails.isFPCall,
                    room: callStore.currentCallDetails.roomId
                });
                if (isFamilyPortalCall.value) {
                    return;
                }

                await messageService.value.unansweredCall(
                    outgoingCallNotification.value.conversationId,
                    outgoingCallNotification.value.roomId
                );
            } catch (error) {
                console.error(error);
            } finally {
                eventBus.emit("updateDisableCallBtn", false);
            }
        };

        const fullNameToInitials = (fullName: string) => {
            if (fullName) {
                const words = fullName.trim().split(/\s+/);
                return words.map((word) => word.charAt(0).toUpperCase()).join("");
            }
            return "";
        };

        onBeforeUnmount(() => {
            stopAudio();
        });

        const handleJitsiMessage = (notification: INotification) => {
            notifications.value.push(notification);
        };

        const handleCallAccept = async (callNotification: ICallSignalNotification) => {
            try {
                const isUserOnCall = callStore.currentCallDetails.isUserOnCall;
                if (isUserOnCall) {
                    acceptNewCallDialog(callNotification);
                } else {
                    if (callNotification.isGroupNotification) {
                        eventBus.emit("joinMeeting", callNotification);
                        closeNotificationPopup();
                    } else {
                        await messageService.value.acceptCall(
                            callSignalRNotification.value.conversationId,
                            callSignalRNotification.value.roomId
                        );
                        eventBus.emit("joinMeeting", callNotification);
                        closeNotificationPopup();
                    }
                }
            } catch (error) {
                console.error(error);
            }
        };

        const acceptNewCallDialog = (callNotification: ICallSignalNotification) => {
            const promptOptions: IPromptOptions = {
                message: "Accepting this call will disconnect your active call",
                okText: "Accept",
                cancelText: "Decline"
            };
            dialog.open(Prompt, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: { promptOptions },
                onClose: async (confirm: any) => {
                    if (confirm.data) {
                        if (callNotification.isGroupNotification) {
                            eventBus.emit("endCurrentCall");
                            eventBus.emit("joinMeeting", callNotification);
                            closeNotificationPopup();
                        } else {
                            await messageService.value.acceptCall(
                                callSignalRNotification.value.conversationId,
                                callSignalRNotification.value.roomId
                            );
                            eventBus.emit("endCurrentCall");
                            eventBus.emit("joinMeeting", callNotification);
                            closeNotificationPopup();
                        }
                    } else {
                        handleCallDecline();
                    }
                }
            });
        };

        const closeNotificationPopup = () => {
            callSignalRNotification.value = null;
            recipientDeclined.value = false;
            stopAudio();
        };

        const handleOutgoingCallNotification = async (notification: IOutgoingCallProps) => {
            outgoingCallNotification.value = notification;
            callControls.value = notification?.callControls;
            isFamilyPortalCall.value = notification?.isFamilyPortalCall;
            if (notification) {
                await playAudioMultipleTimes(1);
            } else {
                stopAudio();
            }
        };

        const closeOutgoingPopup = () => {
            outgoingCallNotification.value = null;
            recipientDeclined.value = false;
            stopAudio();
        };
        const handleAudioChange = (value: boolean) => {
            callControls.value.audioEnabled = value;
        };
        const handleVideoChange = (value: boolean) => {
            callControls.value.videoEnabled = value;
        };
        const handleRetryCall = () => {
            closeOutgoingPopup();
            eventBus.emit("retryCall");
        };

        // Reply functions
        const getReplyState = (conversationId: string): IReplyState => {
            return (
                replyStates.value.get(conversationId) || {
                    conversationId,
                    isReplying: false,
                    replyText: "",
                    isSending: false,
                    isReacting: false
                }
            );
        };

        const isReplyValid = (conversationId: string): boolean => {
            const state = getReplyState(conversationId);
            return state.replyText.trim().length > 0 && !state.isSending;
        };

        const getConversationReplies = (conversationId: string): IReply[] => {
            return replyHistory.value.get(conversationId)?.replies || [];
        };

        // Reply methods
        const showReplyInput = (conversationId: string) => {
            const currentState = getReplyState(conversationId);
            replyStates.value.set(conversationId, {
                ...currentState,
                isReplying: true,
                replyText: ""
            });
        };

        const showReactions = (conversationId: string) => {
            const currentState = getReplyState(conversationId);
            replyStates.value.set(conversationId, {
                ...currentState,
                isReacting: true
            });
        };

        const hideReplyInput = (conversationId: string) => {
            const currentState = getReplyState(conversationId);
            replyStates.value.set(conversationId, {
                ...currentState,
                isReplying: false,
                replyText: "",
                isSending: false,
                isReacting: false
            });
        };

        const updateReplyText = (conversationId: string, text: string) => {
            const currentState = getReplyState(conversationId);
            replyStates.value.set(conversationId, {
                ...currentState,
                replyText: text
            });
        };

        const sendReply = async (conversationId: string) => {
            _analyticsService.value.logEvent({
                category: AnalyticsCategory.AxxessConnect,
                action: "Send Message"
            });
            const currentState = getReplyState(conversationId);

            if (!isReplyValid(conversationId)) {
                return;
            }

            // Set sending state
            replyStates.value.set(conversationId, {
                ...currentState,
                isSending: true
            });

            try {
                const replyId = `reply_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                // Add to reply history immediately with 'sending' status
                const history = replyHistory.value.get(conversationId) || { conversationId, replies: [] };
                history.replies.push({
                    id: replyId,
                    text: currentState.replyText,
                    timestamp: new Date(),
                    status: "sending"
                });
                replyHistory.value.set(conversationId, history);
                const payload: ISendMessageQuery = {
                    message: currentState.replyText,
                    isImportant: false,
                    senderDisplayFirstname: appStore.loggedInUserDetails.firstName,
                    senderDisplayLastname: appStore.loggedInUserDetails.lastName
                };
                // Send the reply via SignalR or MessageService
                const messagesResult = await messageService.value!.sendMessage(conversationId, payload);

                const senderFullName = `${appStore.loggedInUserDetails.firstName} ${appStore.loggedInUserDetails.lastName}`;
                const notification = {
                    messageId: messagesResult.id,
                    modifiedOn: new Date().toISOString().slice(0, -1),
                    text: currentState.replyText,
                    messageAssets: [] as string[],
                    senderName: senderFullName,
                    clicked: false,
                    placeHoldersList: null as any,
                    isImportant: false
                };
                await convStore.updateSideNavMessage(conversationId, notification);
                convStore.moveConversationToTop(conversationId);

                // Update reply status to 'sent'
                // Commenting for now as we might use this in future
                // const updatedHistory = replyHistory.value.get(conversationId);
                // const replyIndex = updatedHistory!.replies.findIndex((r) => r.id === replyId);
                // if (replyIndex > -1) {
                //     updatedHistory!.replies[replyIndex].status = "sent";
                // }

                // Show confirmation message
                _toastrService.value.success({
                    title: `Success`,
                    message: `Reply sent successfully!`
                });

                // Hide reply input
                hideReplyInput(conversationId);

                // Remove the notification after successful reply
                scheduleNotificationRemoval(conversationId, true);
            } catch (error) {
                console.error("Error sending reply:", error);

                // Update reply status to 'failed'
                const history = replyHistory.value.get(conversationId);
                if (history) {
                    const failedReply = history.replies.find((r) => r.status === "sending");
                    if (failedReply) {
                        failedReply.status = "failed";
                    }
                }

                // Reset sending state
                const resetState = getReplyState(conversationId);
                replyStates.value.set(conversationId, {
                    ...resetState,
                    isSending: false
                });

                _toastrService.value.error({
                    title: `Error - Failed`,
                    message: `Failed to send reply. Please try again.`
                });
            }
        };

        const cancelReply = (conversationId: string) => {
            hideReplyInput(conversationId);
        };

        const sendReaction = async (conversationId: string, messageId: string, emoji: string) => {
            const currentState = getReplyState(conversationId);
            // Set sending state
            replyStates.value.set(conversationId, {
                ...currentState,
                isSending: true
            });
            try {
                await messageService.value!.sendReaction(messageId, conversationId, emoji);

                // Reset sending state
                const resetState = getReplyState(conversationId);
                replyStates.value.set(conversationId, {
                    ...resetState,
                    isSending: false
                });

                // Show confirmation message
                _toastrService.value.success({
                    title: `Success`,
                    message: `Reaction sent successfully!`
                });

                // Hide reply input
                hideReplyInput(conversationId);

                // Remove the notification after successful reaction
                scheduleNotificationRemoval(conversationId, true);
            } catch (error) {
                _toastrService.value.error({
                    title: `Error - Failed`,
                    message: `Failed to send reaction. Please try again.`
                });
            }
        };

        onMounted(async () => {
            await initialiseSignalR();
            await setUpNotificationEvents();
            await setUpNotificationCallEvents();
            messageService.value = Container.instance.get(MessageService);
            familyPortalService.value = Container.instance.get(FamilyPortalService);
            userManager.value = Container.instance.get(UserManager);
            _toastrService.value = Container.instance.get(ToastrService);
            _analyticsService.value = Container.instance.get(AnalyticsService);
            eventBus.on("jitsiMessage", handleJitsiMessage);
            eventBus.on("outgoingCallNotification", handleOutgoingCallNotification);
        });

        onBeforeUnmount(() => {
            eventBus.off("jitsiMessage", handleJitsiMessage);
            eventBus.off("outgoingCallNotification", handleOutgoingCallNotification);
        });

        return {
            notifications,
            callControls,
            hasJoinMeetingBtn,
            NotificationTypeEnum,
            ReactionEmojisEnum,
            chatType,
            closeNotificationPopup,
            closeOutgoingPopup,
            handleCallDecline,
            handleOutgoingCallDecline,
            handleCallUnanswered,
            handleCallAccept,
            handleAudioChange,
            handleVideoChange,
            handleRetryCall,
            fullNameToInitials,
            navigateToConversation,
            closeChatNotification,
            scheduleNotificationRemoval,
            callSignalRNotification,
            outgoingCallNotification,
            recipientDeclined,
            markdownToHtml,
            sanitizeMarkup,
            // New reply-related returns
            getReplyState,
            isReplyValid,
            getConversationReplies,
            showReplyInput,
            showReactions,
            hideReplyInput,
            updateReplyText,
            sendReply,
            cancelReply,
            sendReaction
        };
    }
});
